import React, { useState, useEffect, useRef } from 'react';
import { FiUser, FiLogOut, FiMenu, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import { useSidebar } from '../contexts/SidebarContext';
import { subscriptionAPI, SubscriptionStatus, authAPI } from '../services/api';
import ProfileModal from './ProfileModal';
import ThemeToggle from './ThemeToggle';
import './DashboardHeader.css';

interface DashboardHeaderProps {
  userEmail?: string;
  organization?: string;
  onLogout?: () => void;
  /** After the user saves profile (e.g. refresh parent `userInfo`). */
  onProfileUpdated?: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ 
  userEmail, 
  organization,
  onLogout,
  onProfileUpdated,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isStaff, setIsStaff] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { setIsMobileOpen } = useSidebar();

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const profile = await authAPI.getProfile();
        const isStaffUser = profile.is_staff || profile.is_superuser || false;
        setIsStaff(isStaffUser);
        
        if (!isStaffUser) {
          const status = await subscriptionAPI.getSubscriptionStatus();
          setSubscriptionStatus(status);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const getSubscriptionBadge = () => {
    if (loading) return null;
    if (isStaff) return null; // Don't show subscription status for staff/admin
    
    if (!subscriptionStatus?.has_subscription) {
      return (
        <span className="subscription-badge expired">
          {React.createElement(FiXCircle as React.ComponentType<{ size?: number }>, { size: 14 })}
          <span>No Subscription</span>
        </span>
      );
    }

    const subscription = subscriptionStatus.subscription;
    if (!subscription) return null;

    if (subscription.is_valid) {
      const daysRemaining = subscription.days_remaining || 0;
      if (daysRemaining <= 7) {
        return (
          <span className="subscription-badge warning">
            {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 14 })}
            <span>{daysRemaining} days left</span>
          </span>
        );
      }
      return (
        <span className="subscription-badge active">
          {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 14 })}
          <span>Active</span>
        </span>
      );
    }

    return (
      <span className="subscription-badge expired">
        {React.createElement(FiXCircle as React.ComponentType<{ size?: number }>, { size: 14 })}
        <span>Expired</span>
      </span>
    );
  };

  return (
    <header className="dashboard-header">
      {/* Top Header Bar */}
      <div className="dashboard-header-top-bar">
        <div className="dashboard-header-top">
          <div className="dashboard-header-left">
            <button
              className="mobile-menu-button"
              onClick={() => setIsMobileOpen(true)}
              aria-label="Open menu"
            >
              {React.createElement(FiMenu as React.ComponentType<{ size?: number }>, { size: 24 })}
            </button>
          </div>
          <div className="dashboard-header-right">
            <ThemeToggle />
            {(() => {
              const badge = getSubscriptionBadge();
              return badge && (
                <div className="subscription-badge-container">
                  {badge}
                </div>
              );
            })()}
            <div className="dashboard-user-section" ref={dropdownRef}>
              <button 
                className="dashboard-user-info"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <div className="dashboard-user-info-content">
                  <span className="dashboard-user-name">{userEmail || 'anonymous.user@naati.edu'}</span>
                  <span className="dashboard-user-org">{organization || 'NAATI'}</span>
                </div>
                <span className="dashboard-dropdown-arrow">▼</span>
              </button>
              {showDropdown && (
                <div className="dashboard-user-dropdown">
                  <button 
                    type="button"
                    className="dashboard-dropdown-item" 
                    onClick={() => {
                      setShowDropdown(false);
                      setShowProfileModal(true);
                    }}
                  >
                    {React.createElement(FiUser as React.ComponentType<{ size?: number }>, { size: 18 })}
                    View Profile
                  </button>
                  <button 
                    className="dashboard-dropdown-item logout-item" 
                    onClick={() => {
                      if (onLogout) {
                        onLogout();
                      }
                      setShowDropdown(false);
                    }}
                  >
                    {React.createElement(FiLogOut as React.ComponentType<{ size?: number }>, { size: 18 })}
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Red Horizontal Line */}
      <div className="dashboard-header-divider-line"></div>

      <ProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        isStaff={isStaff}
        onProfileUpdated={onProfileUpdated}
      />
    </header>
  );
};

export default DashboardHeader;
